import { HeartIcon } from "@heroicons/react/24/outline";
import { useVerification } from "src/contexts/VerificationContext";
import { useEffect, useState, useCallback, useMemo } from "react";
import BlurredCard from "../BlurredCard";
import { VitalSign } from "src/shared/types";
import Loading from "../Loading";
import { capitalizeStatus } from "src/utils/utils";
import { apiGet } from "src/utils/api";
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from "../table/StaticDataTable";
import { SortDirection } from "../table/useTableData";
import { useCurrentOrganization, useOrganizationContext } from "src/contexts/OrganizationContext";
import TableDateTime, { getDateTimeSortValue } from "../table/TableDateTime";
import TableBadge from "../table/TableBadge";

const statusColorMap: Record<string, string> = {
  final: "bg-emerald-500",
  preliminary: "bg-amber-500",
  amended: "bg-sky-500",
  cancelled: "bg-rose-500",
};

const vitalColumns: StaticDataColumnProps[] = [
  { title: "Name", sortable: true },
  { title: "Date & Time", sortable: true, defaultSort: true, defaultSortDirection: SortDirection.DESC },
  { title: "Value", sortable: true },
  { title: "Reference", sortable: false },
  { title: "Status", sortable: true },
  { title: "Result", sortable: true },
  { title: "Note", sortable: false },
];

const convertVitalsToRows = (vitals: VitalSign[]): StaticDataRow[][] => {
  return vitals.map((vital) => [
    {
      name: "Name",
      value: vital.name,
      element: <span className="text-gray-900">{vital.name}</span>
    },
    {
      name: "Date & Time",
      value: getDateTimeSortValue(vital.date, vital.time),
      element: <TableDateTime date={vital.date} startTime={vital.time} />
    },
    {
      name: "Value",
      value: vital.value || "—",
      element: (
        <span className={`tabular-nums ${vital.isAbnormal ? 'text-rose-600' : 'text-gray-900'}`}>
          {vital.value || "—"} <span className="text-xs">{vital.unit}</span>
        </span>
      )
    },
    {
      name: "Reference",
      value: vital.referenceRange || "—",
      element: <span className="text-sm">{vital.referenceRange || "—"}</span>
    },
    {
      name: "Status",
      value: vital.status,
      element: (
        <TableBadge
          text={capitalizeStatus(vital.status)}
          bgColorClass={statusColorMap[vital.status] ?? "bg-gray-500"}
        />
      )
    },
    {
      name: "Result",
      value: vital.isAbnormal ? 1 : 0,
      element: (
        <TableBadge
          text={vital.isAbnormal ? "Abnormal" : "Normal"}
          bgColorClass={vital.isAbnormal ? "bg-rose-500" : "bg-emerald-500"}
        />
      )
    },
    {
      name: "Note",
      value: vital.note || "—",
      element: <span className="text-gray-600 text-sm">{vital.note || "—"}</span>
    },
  ]);
};

const dummyVitals: VitalSign[] = [
  {
    name: "Vital 1",
    value: "100",
    unit: "bpm",
    date: new Date().toISOString().split('T')[0],
    time: "10:00",
    status: "final",
    isAbnormal: false,
    referenceRange: "100-120 bpm",
    note: "Note 1",
    effectiveDateTime: new Date(),
  },
  {
    name: "Vital 2",
    value: "100",
    unit: "bpm",
    date: new Date().toISOString().split('T')[0],
    time: "10:00",
    status: "final",
    isAbnormal: false,
    referenceRange: "100-120 bpm",
    note: "Note 2",
    effectiveDateTime: new Date(),
  },
];

type VitalsCardProps = {
  yearFrom: string;
  yearTo: string;
  refreshKey?: number;
};

const VitalsCard = ({ yearFrom, yearTo, refreshKey }: VitalsCardProps) => {
  const organization = useCurrentOrganization();
  const { patientId } = useOrganizationContext();
  const [ vitals, setVitals ] = useState<VitalSign[]>([]);
  const [ loading, setLoading ] = useState(false);

  const { isVerified } = useVerification();
  const showLoading = isVerified && loading;

  const fetchVitals = useCallback(async () => {
    if (!organization || !patientId) {
      setVitals([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const params = { yearFrom, yearTo };
      const response = await apiGet(`/api/${organization.id}/patients/${patientId}/vital-signs`, params);
      const data = await response.json();

      if (data.error) {
        setVitals([]);
        window.location.href = "/error";
      }

      const vitals = data.vitalSigns || [];
      vitals.sort((a: VitalSign, b: VitalSign) => {
        if (a.date === 'Unknown date') return 1;
        if (b.date === 'Unknown date') return -1;
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });

      setVitals(vitals);
    } catch (_) {
      setVitals([]);
    } finally {
      setLoading(false);
    }
  }, [ organization, patientId, yearFrom, yearTo ]);

  useEffect(() => {
    if (isVerified) {
      fetchVitals();
    } else {
      setVitals(dummyVitals);
    }
  }, [ isVerified, fetchVitals, yearFrom, yearTo, refreshKey ]);


  const tableRows = useMemo(() =>
    convertVitalsToRows(vitals),
    [ vitals ]
  );

  return (
    <BlurredCard>
      {showLoading ? (
        <div className="flex justify-center mt-8">
          <Loading message="Loading Vitals..." borderColor="border-brand-500" />
        </div>
      ) : (
        <div className="-mx-2">
          {vitals.length > 0 ? (
            <StaticDataTable
              tableId="vitals-table"
              columns={vitalColumns}
              rows={tableRows}
              updateUrlParams={false}
              rowId={(item) => `${item.Name}-${item.Value}`}
            />
          ) : (
            <div className="text-center py-12 text-gray-400">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <HeartIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium">No vitals found</p>
            </div>
          )}
        </div>
      )}
    </BlurredCard>
  );
};

export default VitalsCard;
